import { useRef } from 'react'

// #hero — headline, a "Get a quote" anchor CTA, and the embedded marketing video.
// The mp4 is a documented manual drop-in (see public/assets/video/README.md): until
// it exists the poster shows and video.play() rejects harmlessly (guarded below), so
// a missing/404 source never throws.
export const HeroSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const playDemo = () => {
    const video = videoRef.current
    if (!video) return
    video.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // A missing/404 source makes play() reject — swallow it so nothing throws.
    void video.play().catch(() => undefined)
  }

  return (
    <section className="bg-light bg-opacity-50 border-top border-light position-relative" id="hero">
      <div className="container pt-5 pb-4 position-relative">
        <div className="row">
          <div className="col-lg-8 mx-auto text-center">
            <span className="fw-semibold text-primary fst-italic">RFID student ID &amp; attendance</span>

            <h1 className="my-4 fs-36 fw-bold lh-base">
              RFID Student ID &amp; Attendance — <span className="text-primary">parents informed</span>,
              <span className="fst-italic text-muted"> campuses secure</span>.
            </h1>

            <p className="mb-4 fs-sm text-muted lh-lg">
              Tap a card at the gate and Gatekeeper verifies the student, logs a time-stamped entry or exit, and texts
              the parent or guardian — with exportable attendance reports for the whole campus.
            </p>

            <div className="d-flex gap-1 gap-sm-2 flex-wrap justify-content-center">
              <a className="btn btn-primary rounded-pill py-2 fw-semibold" href="#contact">
                <i className="ti ti-file-invoice fs-xl me-2"></i>Get a quote
              </a>
              <button type="button" className="btn btn-light rounded-pill py-2 fw-semibold" onClick={playDemo}>
                <i className="ti ti-player-play fs-xl me-2"></i>Watch demo
              </button>
            </div>

            <p className="text-muted small mt-3 mb-0">
              Gatekeeper records and verifies taps at the gate — it does not physically restrict access.
            </p>
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-md-10 mx-auto">
            <div className="ratio ratio-16x9 rounded-4 shadow-lg overflow-hidden">
              <video ref={videoRef} controls playsInline preload="metadata">
                {/* No poster — the video's own first frame (branded intro) is the poster.
                    #t=0.1 forces the browser to paint that frame with preload="metadata"
                    (only metadata + one frame loads; the full clip streams on play). */}
                <source src="/assets/video/gatekeeper_marketing.mp4#t=0.1" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
